import { useMemo } from "react";
import useSWR from "swr";

import { fetchTrades } from "lib/api/trades";

// Convert HL PnL (human-readable USDC string e.g. "1.2345") to 1e30 bigint
function hlUsdToE30(val: string | undefined): bigint {
  const n = parseFloat(val || "0");
  if (!isFinite(n) || n === 0) return 0n;
  // Preserve 6 decimal places, then scale to 1e30
  return BigInt(Math.round(n * 1_000_000)) * 10n ** 24n;
}

export type HlBucketStats = {
  bucketLabel: string;
  pnlUsd: bigint;
  realizedPnlUsd: bigint;
  volume: bigint;
  wins: number;
  losses: number;
  winsLossesRatioBps: bigint | undefined;
};

function bucketByLabel(fills: any[], now: number): Record<string, any[]> {
  const todayStart = new Date();
  todayStart.setUTCHours(0, 0, 0, 0);
  const todayStartMs = todayStart.getTime();
  const yearStart = new Date(Date.UTC(new Date().getUTCFullYear(), 0, 1)).getTime();

  return {
    today: fills.filter((f) => f.time >= todayStartMs),
    yesterday: fills.filter((f) => f.time >= todayStartMs - 86_400_000 && f.time < todayStartMs),
    week: fills.filter((f) => f.time >= now - 7 * 86_400_000),
    month: fills.filter((f) => f.time >= now - 30 * 86_400_000),
    year: fills.filter((f) => f.time >= yearStart),
    all: fills,
  };
}

function computeBucketStats(label: string, fills: any[]): HlBucketStats {
  let realizedPnlUsd = 0n;
  let volume = 0n;
  let wins = 0;
  let losses = 0;

  for (const fill of fills) {
    realizedPnlUsd += hlUsdToE30(fill.pnl);

    const notional = parseFloat(fill.size || "0") * parseFloat(fill.price || "0");
    if (isFinite(notional) && notional > 0) {
      volume += BigInt(Math.round(notional * 1_000_000)) * 10n ** 24n;
    }

    const pnlFloat = parseFloat(fill.pnl ?? "0");
    if (pnlFloat > 0) wins++;
    else if (pnlFloat < 0) losses++;
  }

  const total = wins + losses;
  const winsLossesRatioBps = total > 0 ? BigInt(Math.round((wins / total) * 10_000)) : undefined;

  return { bucketLabel: label, pnlUsd: realizedPnlUsd, realizedPnlUsd, volume, wins, losses, winsLossesRatioBps };
}

export type HlAccountStats = {
  summaryByBucket: Record<string, HlBucketStats>;
  /** YYYY-MM-DD → daily PnL in 1e30 bigint */
  dailyPnl: Map<string, bigint>;
  /** YYYY-MM-DD → number of fills */
  dailyCounts: Record<string, number>;
  isLoading: boolean;
};

export function useHlAccountStats(account: string | null | undefined): HlAccountStats {
  const { data, isLoading } = useSWR(
    account ? ["hlAccountStats", account.toLowerCase()] : null,
    () => fetchTrades({ address: account!.toLowerCase(), platform: "hyperliquid", limit: 1000 }),
    { revalidateOnFocus: false, dedupingInterval: 60_000 },
  );

  const fills = useMemo(() => data?.data?.trades ?? [], [data]);

  const summaryByBucket = useMemo(() => {
    const now = Date.now();
    const bucketed = bucketByLabel(fills, now);
    const result: Record<string, HlBucketStats> = {};
    for (const [label, bucketFills] of Object.entries(bucketed)) {
      result[label] = computeBucketStats(label, bucketFills);
    }
    return result;
  }, [fills]);

  const dailyPnl = useMemo(() => {
    const map = new Map<string, bigint>();
    for (const fill of fills) {
      const date = new Date(fill.time).toISOString().slice(0, 10);
      map.set(date, (map.get(date) ?? 0n) + hlUsdToE30(fill.pnl));
    }
    return map;
  }, [fills]);

  const dailyCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const fill of fills) {
      const date = new Date(fill.time).toISOString().slice(0, 10);
      counts[date] = (counts[date] ?? 0) + 1;
    }
    return counts;
  }, [fills]);

  return { summaryByBucket, dailyPnl, dailyCounts, isLoading };
}
